import { Input } from 'antd';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import message from 'antd/lib/message';
import { resendSignUpCode } from 'aws-amplify/auth';
import { useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import giggLogo from '../../../images/gigg-logo.svg';
import { useConfirmUser } from '../../../services/hooks/useUser';
import variables from '../../../styles/variables.module.scss';
import { setPageTitle } from '../../../utils/Utils';
import './Login.scss';

const ConfirmSignUp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { mutate: confirmUser, error: confirmUserError, isPending } = useConfirmUser();

  const [isSending, setIsSending] = useState(false);
  const [resendError, setResendError] = useState('');

  const navigate = useNavigate();

  const [canConfirm] = useState(() => {
    const code = searchParams.get('code');
    const userName = searchParams.get('userName');
    const clientId = searchParams.get('clientId');
    return !!code && !!userName && !!clientId;
  });

  useEffect(() => {
    setPageTitle('Confirm Registration');
  }, []);

  const verify = () => {
    confirmUser(
      {
        code: searchParams.get('code')!,
        userName: searchParams.get('userName')!,
        clientId: searchParams.get('clientId')!,
      },
      {
        onSuccess: () => {
          message.success('Account Verified');
          navigate('/login');
        },
      },
    );
  };

  useEffect(() => {
    if (canConfirm) {
      verify();
    }
  }, []);

  const resendCode = async ({ email }: { email: string }) => {
    setIsSending(true);
    resendSignUpCode({ username: email })
      .then(() => {
        message.success(`Code sent to ${email}`);
      })
      .catch((err: Error) => {
        setResendError(err.message);
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <div className="flex-container justify-center router-wrapper">
      <div className="login-outer">
        <div className="flex-container justify-center">
          <div className="margin-bottom-md">
            <div className="login-image-container">
              <img className="login-img" src={giggLogo} height="auto" width="100%" alt="Gigg" />
            </div>
            <div className="flex-container justify-center">
              <Title canConfirm={canConfirm} isLoading={isPending} />_
            </div>
          </div>
        </div>
        <div className="login-inner">
          <Form layout="vertical" onFinish={resendCode}>
            {(resendError || confirmUserError?.message) && (
              <Error canConfirm={canConfirm} errorMessage={resendError || confirmUserError!.message} />
            )}
            <ResendForm canConfirm={canConfirm} isLoading={isPending || isSending} />
          </Form>
        </div>
      </div>
    </div>
  );
};

const ResendForm = ({ canConfirm, isLoading }: { canConfirm: boolean; isLoading: boolean }) => {
  if (!canConfirm) {
    return (
      <>
        <Form.Item
          name="email"
          validateTrigger="onBlur"
          rules={[
            {
              type: 'email',
              message: 'Invalid email',
            },
            {
              required: true,
              message: 'Please input your email',
            },
          ]}
        >
          <Input className="login-input" placeholder="Email" />
        </Form.Item>
        <div className="flex-container margin-bottom-md">
          <div className="flex-container flex">
            <Button className="login-button" htmlType="submit">
              {isLoading ? (
                <FontAwesomeIcon icon={['fas', 'spinner-third']} size="lg" className="fa-fast-spin" />
              ) : (
                <span>Resend</span>
              )}
            </Button>
          </div>
        </div>
        <div className="flex-container justify-space-between">
          <Link to="/login" className="login-link">
            Sign In
          </Link>
        </div>
      </>
    );
  }
  return null;
};

const Error = ({ canConfirm, errorMessage }: { canConfirm: boolean; errorMessage: string }) => {
  return (
    <Form.Item>
      <div style={{ color: variables.errorColor }} className="text-center">
        {errorMessage}
      </div>
      <div className="text-center">
        {canConfirm ? (
          <Link to="/login" className="login-link">
            Sign In
          </Link>
        ) : (
          ''
        )}
      </div>
    </Form.Item>
  );
};

const Title: React.FC<{ canConfirm: boolean; isLoading: boolean }> = ({ canConfirm, isLoading }) => {
  if (canConfirm && isLoading) {
    return (
      <div className="flex-center flex-column">
        <span className="margin-bottom-sm">Verifying your account...</span>
        <FontAwesomeIcon icon={['fas', 'spinner-third']} size="lg" className="fa-fast-spin" />
      </div>
    );
  }
  return canConfirm ? null : 'Resend Activation Email';
};

export default ConfirmSignUp;
